/**
 * PromoBody — popup-сообщения «не уходите, скидка», «подпишитесь — промокод».
 * Структура отличается от формы: одно сообщение с заголовком, текстом, CTA и
 * картинкой. Триггеры в BehaviorTab плюс специфичные для promo: exit-intent,
 * scroll-depth, time-on-page, idle.
 */
import { defaultTemplateMeta } from '@inbx/shared/schemas';
import { type ReactNode } from 'react';

import {
  type WidgetBodyContract,
  type WidgetDraft,
} from './editor-shell';
import { AssetUpload, BehaviorTab, CodeTab, ThemeTab } from './shared-tabs';

interface PromoContent {
  headline?: string;
  body_text?: string;
  cta_label?: string;
  cta_url?: string;
  dismissible?: boolean;
}

interface PromoMedia {
  image_url?: string;
  image_alt?: string;
  image_position?: 'left' | 'right' | 'top' | 'bg';
}

type PromoTrigger =
  | { type: 'exit_intent' }
  | { type: 'scroll'; depth_percent: number }
  | { type: 'time'; seconds: number }
  | { type: 'idle'; seconds: number };

interface PromoBehavior {
  mode?: 'popup' | 'inline' | 'modal';
  position?: string;
  triggers?: PromoTrigger[];
  show_once_per?: 'session' | 'day' | 'week' | 'forever';
  dismissible?: boolean;
}

export function makePromoBody(args: {
  widgetPublicKey: string;
  workspaceId: string;
}): WidgetBodyContract {
  return {
    getTabs() {
      return [
        { key: 'content', label: 'Содержимое' },
        { key: 'media', label: 'Медиа' },
        { key: 'theme', label: 'Тема' },
        { key: 'behavior', label: 'Поведение' },
        { key: 'triggers', label: 'Триггеры' },
        { key: 'code', label: 'Код' },
      ];
    },
    defaultDraft() {
      // Старт с реального пресета из общего реестра (структурно «настоящего»).
      const t = defaultTemplateMeta('promo');
      return {
        themeConfig: { template_id: t.id },
        contentConfig: { ...(t.defaultContent as WidgetDraft['contentConfig']) },
        behaviorConfig: { ...(t.defaultBehavior ?? {}) } as WidgetDraft['behaviorConfig'],
        mediaConfig: {},
      };
    },
    validate(draft) {
      const errs: string[] = [];
      const c = (draft.contentConfig ?? {}) as PromoContent;
      if (!c.headline?.trim()) errs.push('Заголовок обязателен');
      if (c.cta_url && !/^(https?:\/\/|\/|#|mailto:|tel:)/.test(c.cta_url))
        errs.push('URL CTA должен начинаться с http://, /, # или mailto:');
      return errs;
    },
    renderTab(activeKey, draft, onChange): ReactNode {
      if (activeKey === 'content') return <PromoContentTab draft={draft} onChange={onChange} />;
      if (activeKey === 'media') return <PromoMediaTab draft={draft} onChange={onChange} />;
      if (activeKey === 'theme') return <ThemeTab draft={draft} onChange={onChange} />;
      if (activeKey === 'behavior') return <BehaviorTab draft={draft} onChange={onChange} />;
      if (activeKey === 'triggers') return <PromoTriggersTab draft={draft} onChange={onChange} />;
      if (activeKey === 'code')
        return <CodeTab widgetPublicKey={args.widgetPublicKey} workspaceId={args.workspaceId} />;
      return null;
    },
  };
}

function PromoContentTab({
  draft,
  onChange,
}: {
  draft: WidgetDraft;
  onChange: (patch: Partial<WidgetDraft>) => void;
}): JSX.Element {
  const c = (draft.contentConfig ?? {}) as PromoContent;
  const set = (patch: Partial<PromoContent>): void =>
    onChange({ contentConfig: { ...draft.contentConfig, ...patch } });
  return (
    <div>
      <div className="inbx-editor-section">
        <label className="inbx-editor-label">Заголовок</label>
        <input
          type="text"
          className="inbx-editor-input"
          value={c.headline ?? ''}
          onChange={(e) => set({ headline: e.target.value })}
        />
      </div>
      <div className="inbx-editor-section">
        <label className="inbx-editor-label">Основной текст</label>
        <textarea
          className="inbx-editor-textarea"
          rows={3}
          value={c.body_text ?? ''}
          onChange={(e) => set({ body_text: e.target.value })}
        />
      </div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-row">
          <div>
            <label className="inbx-editor-label">Текст кнопки</label>
            <input
              type="text"
              className="inbx-editor-input"
              value={c.cta_label ?? ''}
              onChange={(e) => set({ cta_label: e.target.value })}
            />
          </div>
          <div>
            <label className="inbx-editor-label">Ссылка кнопки</label>
            <input
              type="text"
              className="inbx-editor-input"
              placeholder="https://… или #"
              value={c.cta_url ?? ''}
              onChange={(e) => set({ cta_url: e.target.value })}
            />
          </div>
        </div>
      </div>
      <div className="inbx-editor-section">
        <label
          style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}
        >
          <input
            type="checkbox"
            checked={c.dismissible ?? true}
            onChange={(e) => set({ dismissible: e.target.checked })}
          />
          Показать кнопку закрытия
        </label>
      </div>
    </div>
  );
}

function PromoMediaTab({
  draft,
  onChange,
}: {
  draft: WidgetDraft;
  onChange: (patch: Partial<WidgetDraft>) => void;
}): JSX.Element {
  const m = (draft.mediaConfig ?? {}) as PromoMedia;
  const set = (patch: Partial<PromoMedia>): void =>
    onChange({ mediaConfig: { ...draft.mediaConfig, ...patch } });
  return (
    <div>
      <div className="inbx-editor-section">
        <label className="inbx-editor-label">Изображение</label>
        <AssetUpload
          accept="image/*"
          value={m.image_url}
          onChange={(url) => set({ image_url: url || undefined })}
        />
      </div>
      <div className="inbx-editor-section">
        <label className="inbx-editor-label">Alt-текст (для доступности)</label>
        <input
          type="text"
          className="inbx-editor-input"
          value={m.image_alt ?? ''}
          onChange={(e) => set({ image_alt: e.target.value })}
        />
      </div>
      <div className="inbx-editor-section">
        <label className="inbx-editor-label">Положение изображения</label>
        <select
          className="inbx-editor-select"
          value={m.image_position ?? 'top'}
          onChange={(e) =>
            set({ image_position: e.target.value as 'left' | 'right' | 'top' | 'bg' })
          }
        >
          <option value="top">Сверху</option>
          <option value="left">Слева</option>
          <option value="right">Справа</option>
          <option value="bg">На фоне (для modal/corner)</option>
        </select>
      </div>
    </div>
  );
}

function PromoTriggersTab({
  draft,
  onChange,
}: {
  draft: WidgetDraft;
  onChange: (patch: Partial<WidgetDraft>) => void;
}): JSX.Element {
  const b = (draft.behaviorConfig ?? {}) as PromoBehavior;
  const triggers = b.triggers ?? [];
  const set = (patch: Partial<PromoBehavior>): void =>
    onChange({ behaviorConfig: { ...draft.behaviorConfig, ...patch } });
  const update = (i: number, patch: Partial<PromoTrigger>): void =>
    set({
      triggers: triggers.map((t, j) => (i === j ? ({ ...t, ...patch } as PromoTrigger) : t)),
    });
  const remove = (i: number): void => set({ triggers: triggers.filter((_, j) => j !== i) });

  return (
    <div>
      <div className="inbx-editor-section">
        <div className="inbx-editor-section__title">Когда показывать</div>
        <div className="inbx-editor-help" style={{ marginBottom: 10 }}>
          Если триггеров несколько — показ при выполнении ЛЮБОГО (OR).
        </div>
        {triggers.map((t, i) => (
          <div
            key={i}
            style={{
              display: 'flex',
              gap: 8,
              alignItems: 'center',
              padding: 10,
              border: '1px solid var(--inbx-border)',
              borderRadius: 6,
              marginBottom: 6,
              background: 'var(--inbx-bg-secondary)',
            }}
          >
            <select
              className="inbx-editor-select"
              style={{ flex: '0 0 200px' }}
              value={t.type}
              onChange={(e) => {
                const nt = e.target.value as PromoTrigger['type'];
                if (nt === 'exit_intent') update(i, { type: nt } as { type: 'exit_intent' });
                else if (nt === 'scroll') update(i, { type: nt, depth_percent: 50 });
                else if (nt === 'time') update(i, { type: nt, seconds: 10 });
                else if (nt === 'idle') update(i, { type: nt, seconds: 30 });
              }}
            >
              <option value="exit_intent">Курсор уходит из окна</option>
              <option value="scroll">Прокручено N%</option>
              <option value="time">N секунд на странице</option>
              <option value="idle">N секунд без активности</option>
            </select>
            {t.type === 'scroll' && (
              <>
                <input
                  type="number"
                  min={1}
                  max={100}
                  className="inbx-editor-input"
                  style={{ flex: '0 0 80px' }}
                  value={t.depth_percent}
                  onChange={(e) => update(i, { type: 'scroll', depth_percent: Number(e.target.value) || 50 })}
                />
                <span style={{ fontSize: 12 }}>%</span>
              </>
            )}
            {(t.type === 'time' || t.type === 'idle') && (
              <>
                <input
                  type="number"
                  min={1}
                  className="inbx-editor-input"
                  style={{ flex: '0 0 80px' }}
                  value={t.seconds}
                  onChange={(e) =>
                    update(i, { type: t.type, seconds: Number(e.target.value) || 10 } as PromoTrigger)
                  }
                />
                <span style={{ fontSize: 12 }}>сек</span>
              </>
            )}
            <button
              type="button"
              className="inbx-btn inbx-btn--danger"
              style={{ marginLeft: 'auto' }}
              onClick={() => remove(i)}
            >
              ✕
            </button>
          </div>
        ))}
        <button
          type="button"
          className="inbx-btn"
          onClick={() => set({ triggers: [...triggers, { type: 'exit_intent' }] })}
        >
          + Триггер
        </button>
      </div>
    </div>
  );
}
